/**
 * THIS IS A SINGLETON.
 * It is to be instantiated only once.
 * Please use state/toast instead.
 */
import * as React from 'react';
import { Transition } from '@headlessui/react';
import { MessageDescriptor } from '@lingui/core';
import Icon from 'components/Icon';
import { Link } from 'react-router';
import { classNames } from 'utils/ui/css';
import toastNotification from 'state/toast';
import { useLingui } from '@lingui/react';

export type TToastType = 'success' | 'link' | 'info' | 'warn' | 'error';

type TToastTypeStyles = {
  [key in TToastType]: {
    icon: string | string[];
    border: string;
    bg: string;
    text: string;
  };
};

const toastStyle: TToastTypeStyles = {
  success: {
    icon: 'ex3d-icon--success bg-green-700 text-green-700',
    border: 'border-green-700',
    bg: 'bg-green-100',
    text: 'text-green-700',
  },
  link: {
    icon: 'ex3d-icon--external-link bg-teal text-gray-400',
    border: 'border-teal',
    bg: 'bg-white',
    text: 'text-teal',
  },
  info: {
    icon: 'ex3d-icon--info bg-blue-700 text-blue-700',
    border: 'border-blue-700',
    bg: 'bg-blue-100',
    text: 'text-blue-700',
  },
  warn: {
    icon: 'ex3d-icon--warning bg-orange-800 text-orange-800',
    border: 'border-orange-800',
    bg: 'bg-orange-100',
    text: 'text-orange-800',
  },
  error: {
    icon: 'ex3d-icon--error bg-red-700 text-red-700',
    border: 'border-red-700',
    bg: 'bg-red-100',
    text: 'text-red-700',
  },
};
export interface IToastProps {
  type: TToastType;
  title: string | MessageDescriptor;
  notificationId: string;
  message?: string | MessageDescriptor;
  linkText?: string;
  to?: string;
  onClick?: (e?: React.MouseEvent<HTMLElement>) => boolean | undefined;
  newTab?: boolean;
  timeout?: number;
  onToggle?: (show: boolean) => void;
}

export default function Toast() {
  const i18n = useLingui();
  const translatedMessage = (message: string | MessageDescriptor): string =>
    typeof message === 'string' ? message : i18n._(message);
  const props = toastNotification.useState();
  const styles = toastStyle[props.type ?? 'info'];
  classNames([styles.icon, styles.text, styles.bg, styles.border]);

  // destructuring only this prop to optimize useCallback updates
  const { onClick, timeout, show } = props;

  // Reset rendering flag when Toast unmounts (cleanup)
  React.useEffect(() => {
    return () => {
      toastNotification.setValues({ isBeingRendered: false });
    };
  }, []);

  const onClickClose = React.useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      let response: boolean | undefined = true;
      if (onClick) {
        response = onClick(event);
      }

      if (response !== false) {
        toastNotification.setValues({ show: false });
      }
    },
    [onClick]
  );

  React.useEffect(() => {
    if (timeout && show) {
      const timer = setTimeout(() => {
        toastNotification.setValues({ show: false });
      }, timeout);
      return () => clearTimeout(timer);
    }
  }, [timeout, show]);

  React.useEffect(() => {
    if (props.onToggle) props.onToggle(!!show);
  }, [props, show]);

  return (
    <>
      {/* Global notification live region, render this permanently at the end of the document */}
      <aside
        data-testid="toast-component"
        role="dialog"
        aria-live="assertive"
        className="pointer-events-none absolute inset-0 z-[101] flex items-end px-4 py-6 sm:items-start sm:p-6"
      >
        <div className="flex w-full flex-col items-center space-y-4 sm:items-end">
          {/* Notification panel, dynamically insert this into the live region when it needs to be displayed */}
          <Transition show={show}>
            <div
              className={`pointer-events-auto w-full max-w-sm overflow-hidden rounded-lg border-2 ${styles.border} ${styles.bg} shadow-lg ring-1 ring-black/5 transition data-[closed]:data-[enter]:translate-y-2 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-100 data-[enter]:ease-out data-[leave]:ease-in data-[closed]:data-[enter]:sm:translate-x-2 data-[closed]:data-[enter]:sm:translate-y-0`}
            >
              <div className="p-4">
                <div className="flex items-start">
                  <div className="shrink-0">
                    <Icon aria-hidden="true" className={`${styles.icon} size-6`} />
                  </div>
                  <div className="ml-3 w-0 flex-1 pt-0.5">
                    <p className={`text-sm font-bold ${styles.text}`}>{translatedMessage(props.title)}</p>
                    <p className={`mt-1 text-sm text-gray-900`}>{translatedMessage(props.message!)}</p>
                    {props.to && (
                      <div className="mt-3 flex space-x-7">
                        <Link
                          to={props.to ?? ''}
                          onClick={onClickClose}
                          target={props.newTab ? '_blank' : undefined}
                          type="button"
                          className="rounded-md text-sm font-bold text-blue-700 hover:text-blue-500 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2"
                        >
                          {props.linkText}
                        </Link>
                      </div>
                    )}
                  </div>
                  <div className="ml-4 flex shrink-0">
                    <button
                      type="button"
                      onClick={() => {
                        toastNotification.setValues({ show: false });
                      }}
                      className="inline-flex rounded-md text-gray-900 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-100 focus:ring-offset-2"
                    >
                      <span className="sr-only">Close</span>
                      <Icon aria-hidden="true" className="ex3d-icon--close size-5 bg-teal" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </Transition>
        </div>
      </aside>
    </>
  );
}
